import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as RestrictionActions from './restriction.actions';
import { mergeMap, map, catchError, of, switchMap, tap, concatMap } from 'rxjs';
import {RestrictionService} from "@core/services/restriction.service";
import {Router} from '@angular/router';
import { TaskDefinitionStatus } from '@core/models/domain/restriction.domain';

@Injectable()
export class RestrictionEffects {
  constructor(
    private actions$: Actions,
    private restrictionService: RestrictionService,
    private router: Router
  ) {}

  // LOAD TASK DEFINITIONS
  loadTaskDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.loadTaskDefinitions),
      // switchMap: concurrent duplicate dispatches (dashboard + 10s auto-reload) share
      // one request instead of racing. loadTaskExecutions below stays mergeMap because
      // loads for different dates are legitimate concurrent requests.
      switchMap(({ statuses }) =>
        this.restrictionService.loadTaskDefinitions(statuses).pipe(
          map((taskDefinitions) =>
            RestrictionActions.loadTaskDefinitionsSuccess({ taskDefinitions })
          ),
          catchError((error) => {
            console.error('Error loading task definitions:', error);
            return of(RestrictionActions.loadTaskDefinitionsFailure({ error: error }));
          })
        )
      )
    )
  );

  // LOAD TASK EXECUTIONS
  loadTaskExecutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.loadTaskExecutions),
      mergeMap(({ date }) =>
        this.restrictionService.loadTaskExecutions(date).pipe(
          map((taskExecutions) => {
            const dateString = JSON.stringify(date);
            return RestrictionActions.loadTaskExecutionsSuccess({ date: dateString, taskExecutions });
          }),
          catchError((error) => {
            console.error('Error loading task executions:', error);
            return of(RestrictionActions.loadTaskExecutionsFailure({ error: error }));
          })
        )
      )
    )
  );

  // CREATE TASK DEFINITION
  createTaskDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.createTaskDefinition),
      mergeMap(({ request }) =>
        this.restrictionService.createTaskDefinition(request).pipe(
          map((created) =>
            RestrictionActions.createTaskDefinitionSuccess({taskDefinition: created,})
          ),
          catchError((error) => {
            console.error('Error creating task definition:', error);
            return of(RestrictionActions.createTaskDefinitionFailure({ error: error }));
          })
        )
      )
    )
  );

  createTaskDefinitionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.createTaskDefinitionSuccess),
      tap(() => {
        setTimeout(() => {
          this.router.navigate(["/restriction/list"]);
        }, 1500);
      })
    ), { dispatch: false }
  );

  // UPDATE TASK DEFINITION
  updateTaskDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.updateTaskDefinition),
      mergeMap(({ request }) =>
        this.restrictionService.updateTaskDefinition(request).pipe(
          map((updated) => {
            if (updated) {
              return RestrictionActions.updateTaskDefinitionSuccess({ taskDefinition: updated, request });
            } else {
              return RestrictionActions.updateTaskDefinitionFailure({ error: "Update failed", request });
            }
          }),
          catchError((error) => {
            console.error('Error updating task definition:', error);
            return of(RestrictionActions.updateTaskDefinitionFailure({ error: error, request }));
          })
        )
      )
    )
  );

  // Reload definitions after a successful update: a start-date change is a
  // deactivate+recreate on the backend (new definition id, old one INACTIVE), so the
  // stored list must be refreshed rather than patched.
  updateTaskDefinitionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.updateTaskDefinitionSuccess),
      concatMap(() => [
        RestrictionActions.loadTaskDefinitions({ statuses: [TaskDefinitionStatus.ACTIVE, TaskDefinitionStatus.INACTIVE] })
      ])
    )
  );

  // Reload definitions after a failed update so the optimistic patch applied on
  // dispatch (no longer shielded — the failure reducer dropped its stamp) is
  // replaced by the server state.
  updateTaskDefinitionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.updateTaskDefinitionFailure),
      concatMap(() => [
        RestrictionActions.loadTaskDefinitions({ statuses: [TaskDefinitionStatus.ACTIVE, TaskDefinitionStatus.INACTIVE] })
      ])
    )
  );

  // CANCEL TASK
  cancelTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.cancelTask),
      mergeMap(({ request, date }) =>
        this.restrictionService.cancelTask(request).pipe(
          map((deletedId) => RestrictionActions.cancelTaskSuccess({ id: deletedId, date })),
          catchError((error) => {
            console.error('Error cancelling task:', error);
            return of(RestrictionActions.cancelTaskFailure({ error: error, request, date }));
          })
        )
      )
    )
  );

  // Reload data after successful task cancellation
  cancelTaskSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.cancelTaskSuccess),
      tap(() => console.log('✅ Task cancelled successfully on server')),
      // Don't reload immediately - the optimistic update already removed it from UI
      // Just reload executions to ensure they're current, but skip reloading definitions
      // The auto-reload will pick up any server changes after a few seconds
      concatMap(({ date }) => {
        const localDate = JSON.parse(date);
        return [
          RestrictionActions.loadTaskExecutions({ date: localDate })
        ];
      })
    )
  );

  // Reload data after failed task cancellation to restore optimistic update
  cancelTaskFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.cancelTaskFailure),
      tap(({ error }) => console.error('❌ Task cancellation failed, reloading original state:', error)),
      concatMap(() => {
        // Reload both definitions and executions to restore correct state
        return [
          RestrictionActions.loadTaskDefinitions({ statuses: [TaskDefinitionStatus.ACTIVE, TaskDefinitionStatus.INACTIVE] })
        ];
      })
    )
  );

  // LOAD PARENT CANCELLATIONS
  loadParentCancellations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.loadParentCancellations),
      mergeMap(({ childId }) =>
        this.restrictionService.loadParentCancellations(childId).pipe(
          map((cancellations) =>
            RestrictionActions.loadParentCancellationsSuccess({ cancellations })
          ),
          catchError((error) => {
            console.error('Error loading parent cancellations:', error);
            return of(RestrictionActions.loadParentCancellationsFailure({ error }));
          })
        )
      )
    )
  );

  // LOAD SCHOOL PARENT CANCELLATIONS
  loadSchoolParentCancellations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestrictionActions.loadSchoolParentCancellations),
      mergeMap(() =>
        this.restrictionService.loadParentCancellationsBySchool().pipe(
          map((cancellations) =>
            RestrictionActions.loadSchoolParentCancellationsSuccess({ cancellations })
          ),
          catchError((error) => {
            console.error('Error loading school parent cancellations:', error);
            return of(RestrictionActions.loadSchoolParentCancellationsFailure({ error }));
          })
        )
      )
    )
  );
}